import { TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    DAY_PARTS,
    type DayPartKey,
} from '@apps/web/features/coverage/model/coverage_types';
import {
    COVERAGE_COLUMNS,
    type CoverageArea,
    type CoverageBucket,
    LOW_COVERAGE_THRESHOLD,
    maxCoverageCount,
} from '@apps/web/features/coverage/model/rollup_model';
import { cn } from '@apps/web/lib/utils';

const AREA_LABELS: Record<CoverageArea, string> = {
    office: 'Office',
    city: 'City',
    zip: 'ZIP',
};

const COVERAGE_PAGE_SIZE = 50;

// Sequential single-hue ramp (magnitude), light to dark. The count is
// printed in every cell so color is never the only channel; low/zero
// cells additionally carry an icon + text.
const RAMP = [
    'bg-transparent',
    'bg-blue-100 dark:bg-blue-950',
    'bg-blue-200 dark:bg-blue-900',
    'bg-blue-300 dark:bg-blue-800',
    'bg-blue-500 text-white',
    'bg-blue-600 text-white',
    'bg-blue-700 text-white',
];

function rampClass(count: number, max: number): string {
    if (count === 0) {
        return 'bg-transparent';
    }
    const step = Math.max(1, Math.ceil((count / max) * (RAMP.length - 1)));
    return RAMP[step] ?? 'bg-blue-700 text-white';
}

function partLabel(dayPart: DayPartKey): string {
    return DAY_PARTS.find((part) => part.key === dayPart)?.label ?? dayPart;
}

export function CoverageAreaPane({
    coverage,
    area,
    onAreaChange,
    onSelectCell,
    onClearFilters,
}: {
    readonly coverage: ReadonlyArray<CoverageBucket>;
    readonly area: CoverageArea;
    readonly onAreaChange: (area: CoverageArea) => void;
    readonly onSelectCell: (
        areaKey: string,
        group: 'weekday' | 'weekend',
        dayPart: DayPartKey,
    ) => void;
    readonly onClearFilters?: () => void;
}) {
    const [visibleBucketCount, setVisibleBucketCount] =
        useState(COVERAGE_PAGE_SIZE);
    useEffect(() => {
        setVisibleBucketCount(COVERAGE_PAGE_SIZE);
    }, [coverage, area]);
    const max = maxCoverageCount(coverage);
    const visibleCoverage = coverage.slice(0, visibleBucketCount);
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
                <div
                    className="border-border inline-flex overflow-hidden rounded-lg border"
                    role="group"
                    aria-label="Group coverage by"
                >
                    {(Object.keys(AREA_LABELS) as Array<CoverageArea>).map(
                        (option) => (
                            <button
                                key={option}
                                type="button"
                                aria-pressed={area === option}
                                onClick={() => onAreaChange(option)}
                                className={cn(
                                    'px-3 py-1.5 text-sm font-medium',
                                    area === option
                                        ? 'bg-primary text-primary-foreground'
                                        : 'text-muted-foreground hover:text-foreground',
                                )}
                            >
                                {AREA_LABELS[option]}
                            </button>
                        ),
                    )}
                </div>
                <p className="text-muted-foreground text-xs">
                    Caregivers available on at least one matching day.
                    Caregivers with no availability data are excluded, so a
                    sparse row is a gap, not coverage. Click a cell to see those
                    caregivers.
                </p>
            </div>
            {coverage.length === 0 ? (
                <div className="border-border rounded-xl border border-dashed py-12 text-center">
                    <p className="text-muted-foreground text-sm">
                        No coverage areas match the current filters.
                    </p>
                    {onClearFilters ? (
                        <Button
                            variant="outline"
                            size="sm"
                            className="mt-3"
                            onClick={onClearFilters}
                        >
                            Clear filters
                        </Button>
                    ) : null}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-[720px] border-separate border-spacing-0.5">
                        <thead>
                            <tr>
                                <th scope="col" colSpan={2} />
                                <th
                                    scope="colgroup"
                                    colSpan={4}
                                    className="text-muted-foreground border-border border-b p-1 text-xs font-semibold"
                                >
                                    Weekdays
                                </th>
                                <th
                                    scope="colgroup"
                                    colSpan={4}
                                    className="text-muted-foreground border-border border-b p-1 text-xs font-semibold"
                                >
                                    Weekend
                                </th>
                            </tr>
                            <tr>
                                <th
                                    scope="col"
                                    className="text-muted-foreground p-1 text-left text-xs font-semibold"
                                >
                                    {AREA_LABELS[area]}
                                </th>
                                <th
                                    scope="col"
                                    className="text-muted-foreground p-1 text-right text-xs font-semibold"
                                >
                                    Roster
                                </th>
                                {COVERAGE_COLUMNS.map((column) => (
                                    <th
                                        key={`${column.group}-${column.dayPart}`}
                                        scope="col"
                                        className="text-muted-foreground p-1 text-center text-xs font-semibold"
                                    >
                                        {partLabel(column.dayPart)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {visibleCoverage.map((bucket) => (
                                <tr key={bucket.areaKey}>
                                    <th
                                        scope="row"
                                        className="p-1 pr-3 text-left text-sm font-medium whitespace-nowrap"
                                    >
                                        {bucket.areaLabel}
                                    </th>
                                    <td className="text-muted-foreground p-1 text-right text-xs whitespace-nowrap tabular-nums">
                                        {bucket.rosterCount}
                                    </td>
                                    {bucket.cells.map((cell) => {
                                        const { group, dayPart } = cell.column;
                                        const count = cell.availableCount;
                                        const low =
                                            count <= LOW_COVERAGE_THRESHOLD;
                                        return (
                                            <td
                                                key={`${group}-${dayPart}`}
                                                className="p-0"
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        onSelectCell(
                                                            bucket.areaKey,
                                                            group,
                                                            dayPart,
                                                        )
                                                    }
                                                    aria-label={`${bucket.areaLabel}, ${group} ${partLabel(dayPart)}: ${count} caregivers available. Show them in the grid.`}
                                                    className={cn(
                                                        'hover:ring-primary flex min-h-11 w-full min-w-18 flex-col items-center justify-center rounded-md px-1 py-1.5 hover:ring-2',
                                                        rampClass(count, max),
                                                        count === 0 &&
                                                            'ring-1 ring-red-400',
                                                    )}
                                                >
                                                    <span className="text-sm font-semibold tabular-nums">
                                                        {count}
                                                    </span>
                                                    {low ? (
                                                        <span className="flex items-center gap-0.5 text-[10px] font-medium">
                                                            <TriangleAlert
                                                                className="size-2.5"
                                                                aria-hidden="true"
                                                            />
                                                            {count === 0
                                                                ? 'none'
                                                                : 'low'}
                                                        </span>
                                                    ) : null}
                                                </button>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {visibleBucketCount < coverage.length ? (
                <div className="flex items-center justify-between gap-3">
                    <p className="text-muted-foreground text-xs">
                        Showing {visibleBucketCount} of {coverage.length} areas
                    </p>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                            setVisibleBucketCount((count) =>
                                Math.min(
                                    coverage.length,
                                    count + COVERAGE_PAGE_SIZE,
                                ),
                            )
                        }
                    >
                        Show more areas
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
